import {
  CircleCheckIcon,
  TriangleAlertIcon,
  Undo2Icon,
  XIcon,
} from 'lucide-react'
import { useState } from 'react'

import { AutoResizeTextarea, Button, Loader } from '@/components/ui'
import {
  type InstructionCheck,
  type MissingRow,
  RECONCILIATION_STATUSES,
  type StatementSegment,
  useProject,
  useStatements,
} from '@/modules/projects'
import {
  gapVerdict,
  layoutSiblings,
  siblingsAlreadyBalanced,
  siblingsAwaitingNote,
  siblingsFixedByNote,
  siblingsStillToFix,
  totalGap,
} from '@/modules/projects/components/reconciliation/helpers'
import {
  isReconciled,
  isStatementProcessing,
  isStatementSegmentStatus,
} from '@/modules/projects/status'
import {
  pendingFixActions,
  usePendingFix,
} from '@/modules/projects/stores/pendingFixes'
import { cn, formatDate, numberToCurrency } from '@/utils'
import { FixedSiblingList } from './FixedSiblingList'
import { LayoutSiblingList } from './LayoutSiblingList'

const CARD =
  'rounded-lg border border-brand/50 bg-card px-4 py-3 text-sm shadow-sm'

// Take the user's note about what we read wrong on this statement and check it. A check that names
// the missing printed lines adds them straight to the cycle; anything else reads the pages again.
// Statements with the same format follow the same path. Undo drops the note.
export const CorrectionPanel = ({
  segment,
  rowCount,
  onCheck,
  onReExtract,
  onInsertRows,
  onUndo,
}: {
  segment: StatementSegment
  // Null until this cycle's rows have loaded, so the "Now" line never shows the last cycle's count.
  rowCount: number | null
  onCheck: (instruction: string) => Promise<InstructionCheck | null>
  onReExtract: (
    instruction: string,
    scope: 'cycle' | 'family',
  ) => Promise<boolean>
  onInsertRows: (instruction: string, rows: MissingRow[]) => Promise<boolean>
  onUndo: () => Promise<boolean>
}) => {
  // Enrichment runs after extraction, so a cycle is still busy while it says categorizing. Undo and
  // re-extract are both refused server-side until it lands.
  const running = isStatementSegmentStatus(
    segment,
    'enqueued',
    'extracting',
    'categorizing',
  )
  const fixApplied = !!segment.instruction && !!segment.beforeReextract
  const projectSlug = useProject()?.slug
  const savedFix = usePendingFix(projectSlug, segment.id)
  // A fix already running or applied has moved past the panel, so its saved copy is stale.
  const restoredFix = running || fixApplied ? null : savedFix

  const [draft, setDraft] = useState(
    restoredFix?.note ?? segment.instruction ?? '',
  )
  const [triage, setTriage] = useState<InstructionCheck | null>(
    restoredFix?.triage ?? null,
  )
  const [checking, setChecking] = useState(false)
  const [adding, setAdding] = useState(false)
  const [open, setOpen] = useState(!!restoredFix)
  const [familyFixCount, setFamilyFixCount] = useState<number | null>(null)
  const [sendingFamilyFix, setSendingFamilyFix] = useState(false)

  const note = draft.trim()
  // A fix checked before the missing-line answer shipped has no rows saved.
  const missingRows = triage?.rows ?? []
  const statements = useStatements()
  const siblings = layoutSiblings(statements, segment)
  // A sibling that already balances is never re-run: it costs money and the run can break it.
  // The family note is served on every sibling before any of them ran it, so siblings are sorted
  // by the note this cycle actually ran: nothing is excluded until a fix has been applied here.
  const noteThatRan = fixApplied
    ? (segment.beforeReextract?.instruction ?? null)
    : null
  const siblingsToFix = siblingsStillToFix(siblings, noteThatRan)
  const fixedSiblings = siblingsFixedByNote(siblings, noteThatRan)
  const balancedSiblingCount = siblingsAlreadyBalanced(
    siblings,
    noteThatRan,
  ).length
  // Siblings sent for a fresh read turn busy, but the added-lines path lands without a run, so a
  // status alone never tells us the family run ended. The note stamped on each sibling does.
  const familyRunning = siblingsToFix.some(({ segment: cycle }) =>
    isStatementSegmentStatus(cycle, 'enqueued', 'extracting', 'categorizing'),
  )
  const familyFixBusy =
    sendingFamilyFix ||
    familyRunning ||
    (familyFixCount !== null &&
      siblingsAwaitingNote(siblingsToFix, segment.instruction).length > 0)
  const parentStatement = statements.find((statement) =>
    statement.segments?.some((candidate) => candidate.id === segment.id),
  )
  const balanced = isReconciled(segment)
  // Matches the server gate: only a finished run that balanced with this exact note proves the fix.
  const fixProven =
    balanced &&
    !segment.manuallyReconciled &&
    isStatementSegmentStatus(segment, 'succeeded') &&
    !!parentStatement &&
    !isStatementProcessing(parentStatement) &&
    segment.beforeReextract?.instruction === segment.instruction
  const othersLabel = otherStatementsLabel(siblingsToFix.length)
  const gap = totalGap(segment.reconciliationDetail?.accounts ?? [])

  const nextStepLine = () => {
    if (siblingsToFix.length > 0) {
      return `If this statement balances, you can then fix the ${othersLabel}.`
    }
    if (balancedSiblingCount > 0) {
      return allOthersBalanceLine(balancedSiblingCount)
    }
    return 'No other statements in this project have this format.'
  }

  // A cycle can miss review with a zero gap when extra rows cancel each other out, so a gap of zero
  // never prints an amount.
  const verdictHeadline = () => {
    if (balanced) return 'This statement now balances'
    if (gap > 0) {
      return `This statement is still off by ${numberToCurrency(gap)}`
    }
    return 'This statement still needs review'
  }

  const rememberFix = (checked: InstructionCheck) => {
    if (projectSlug) {
      pendingFixActions().savePendingFix(projectSlug, segment.id, note, checked)
    }
  }

  const forgetFix = () => {
    if (projectSlug) {
      pendingFixActions().clearPendingFix(projectSlug, segment.id)
    }
  }

  const check = () => {
    if (!note || checking) return
    setChecking(true)
    onCheck(note)
      .then((checked) => {
        setTriage(checked)
        if (checked) rememberFix(checked)
        else forgetFix()
      })
      .finally(() => setChecking(false))
  }

  const changeFix = () => {
    setTriage(null)
    forgetFix()
  }

  // A real undo drops this cycle's note, so the family run we were tracking is no longer ours to
  // wait on. A refused undo leaves that run live, so the count stays and keeps the button disabled.
  const undo = () => {
    forgetFix()
    onUndo().then((undone) => {
      if (undone) setFamilyFixCount(null)
    })
  }

  // Keep the paid check until the run really starts, so a refused run can be retried for free.
  const reExtractCycle = () => {
    if (!note) return
    setTriage(null)
    setOpen(false)
    onReExtract(note, 'cycle').then((started) => {
      if (started) forgetFix()
    })
  }

  // The added lines land without a run, so nothing on the siblings turns busy to show it started.
  const insertFix = () => {
    if (!note || adding) return
    setAdding(true)
    onInsertRows(note, missingRows)
      .then((inserted) => {
        if (!inserted) return
        forgetFix()
        setTriage(null)
        setOpen(false)
      })
      .finally(() => setAdding(false))
  }

  // Siblings get the note the cycle already ran with, never an unchecked draft.
  const applyToFamily = () => {
    if (!segment.instruction || sendingFamilyFix) return
    setFamilyFixCount(siblingsToFix.length)
    setSendingFamilyFix(true)
    onReExtract(segment.instruction, 'family')
      .then((started) => {
        if (!started) setFamilyFixCount(null)
      })
      .finally(() => setSendingFamilyFix(false))
  }

  if (running) {
    return (
      <div className={`${CARD} space-y-1 text-ink-subtle`}>
        <p className="flex items-center gap-2 font-medium text-blue-7">
          <Loader />
          Reading this statement again with your fix…
        </p>
        {segment.instruction && <NoteLine note={segment.instruction} />}
      </div>
    )
  }

  if (segment.instruction && segment.beforeReextract) {
    return (
      <div
        className={cn(
          'space-y-3 rounded-lg border px-4 py-3 text-sm shadow-sm',
          balanced
            ? 'border-emerald-300 bg-emerald-50'
            : 'border-amber-300 bg-amber-50',
        )}
      >
        <div className="flex items-start gap-2">
          {balanced ? (
            <CircleCheckIcon className="mt-0.5 size-4 shrink-0 text-emerald-600" />
          ) : (
            <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-amber-700" />
          )}
          <div className="min-w-0 space-y-1.5">
            <p className="font-semibold text-foreground">{verdictHeadline()}</p>
            <NoteLine note={segment.instruction} />
          </div>
        </div>

        {rowCount != null && (
          <div className="grid grid-cols-2 divide-x rounded-lg border bg-white">
            <VerdictColumn
              caption="Before"
              snapshot={segment.beforeReextract}
              rowCount={segment.beforeReextract.rowCount}
            />
            <VerdictColumn
              caption="After"
              snapshot={{
                status: segment.reconciliationStatus,
                accounts: segment.reconciliationDetail?.accounts ?? [],
              }}
              rowCount={rowCount}
            />
          </div>
        )}

        {fixedSiblings.length > 0 && (
          <FixedSiblingList siblings={fixedSiblings} />
        )}
        {siblingsToFix.length > 0 && fixProven && (
          <LayoutSiblingList siblings={siblingsToFix} />
        )}
        {siblingsToFix.length === 0 && balancedSiblingCount > 0 && (
          <p className="text-muted-foreground text-xs">
            {allOthersBalanceLine(balancedSiblingCount)}
          </p>
        )}
        {siblingsToFix.length > 0 && !fixProven && (
          <p className="text-muted-foreground text-xs">
            This fix hasn't balanced this statement, so it stays off the{' '}
            {othersLabel}.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-1">
          {siblingsToFix.length > 0 && fixProven && (
            <Button
              theme="default"
              size="sm"
              className="cursor-pointer"
              disabled={familyFixBusy}
              onClick={applyToFamily}
            >
              Fix the {othersLabel}
            </Button>
          )}
          <Button
            theme="outline"
            size="sm"
            className="cursor-pointer"
            onClick={undo}
          >
            <Undo2Icon className="size-3.5" />
            Undo this fix
          </Button>
          {familyFixBusy && familyFixCount !== null && (
            <p className="flex items-center gap-2 text-muted-foreground text-xs">
              <Loader />
              We're fixing the {otherStatementsLabel(familyFixCount)}. Each one
              updates here when it's done.
            </p>
          )}
        </div>
      </div>
    )
  }

  if (open) {
    const planReady = triage?.kind === 'plan'
    const editing = !planReady

    return (
      <div className={`${CARD} space-y-3 text-blue-7`}>
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-blue-7">
            What's missing or wrong on this statement?
          </p>
          <Button
            theme="ghost"
            size="sm"
            isIcon
            className="cursor-pointer"
            onClick={() => setOpen(false)}
          >
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </Button>
        </div>

        {editing && (
          <AutoResizeTextarea
            className="border-blue-3 bg-white text-blue-7 focus-visible:ring-2 focus-visible:ring-brand disabled:bg-blue-1 disabled:italic disabled:opacity-100"
            disabled={checking}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                check()
              }
            }}
            placeholder="Describe the fix, like: the Interest Charged line is missing"
          />
        )}

        {checking && <CheckProgress />}

        {!checking && planReady && (
          <section className="space-y-1 border-brand border-l-2 pl-3">
            <p className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
              The fix
            </p>
            <p className="text-foreground">{triage.change}</p>
            {missingRows.length > 0 && <MissingRowList rows={missingRows} />}
          </section>
        )}

        {!checking && triage && !planReady && (
          <p className="border-brand border-l-2 pl-3 text-foreground">
            {triageMessage(triage)}
          </p>
        )}

        {!checking && planReady && siblingsToFix.length > 0 && (
          <LayoutSiblingList siblings={siblingsToFix} />
        )}

        {!checking && !triage && (
          <p className="text-ink-subtle text-xs">
            We read your fix first, then show you what will change.
          </p>
        )}

        {editing && (
          <Button
            theme="default"
            size="sm"
            className="cursor-pointer"
            disabled={!note || checking}
            onClick={check}
          >
            {triage ? 'Check again' : 'Check this fix'}
          </Button>
        )}

        {!editing && (
          <>
            {adding && <AddingProgress missingRowCount={missingRows.length} />}
            <div className="flex flex-wrap items-center gap-2">
              {planReady && (
                <Button
                  theme="default"
                  size="sm"
                  className="cursor-pointer"
                  disabled={adding}
                  onClick={missingRows.length > 0 ? insertFix : reExtractCycle}
                >
                  Fix this statement
                </Button>
              )}
              <Button
                theme="outline"
                size="sm"
                className="cursor-pointer"
                disabled={adding}
                onClick={changeFix}
              >
                Change the fix
              </Button>
            </div>
            {planReady && (
              <p className="text-ink-subtle text-xs">{nextStepLine()}</p>
            )}
            {planReady &&
              missingRows.length === 0 &&
              segment.manuallyCorrectedAt && (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 text-xs">
                  This will replace any transactions you edited by hand on this
                  statement. Undo will not bring them back.
                </p>
              )}
          </>
        )}
      </div>
    )
  }

  if (balanced) {
    return (
      <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        This statement balances.
        <Button
          theme="link"
          size="sm"
          className="h-auto cursor-pointer p-0 text-xs"
          onClick={() => setOpen(true)}
        >
          Something still wrong? Tell us.
        </Button>
      </p>
    )
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="flex items-center gap-2 text-sm text-amber-800">
        <TriangleAlertIcon className="size-4 shrink-0" />
        <span>
          Off by {numberToCurrency(gap)}. If we read these pages wrong, tell us
          what to fix.
        </span>
      </div>
      <Button
        theme="outline"
        size="sm"
        className="shrink-0 cursor-pointer border-amber-300 text-amber-800 hover:bg-amber-100"
        onClick={() => setOpen(true)}
      >
        Something missing or wrong? Tell us
      </Button>
    </div>
  )
}

const MissingRowList = ({ rows }: { rows: MissingRow[] }) => (
  <ul className="divide-y rounded-lg border bg-white text-sm">
    {rows.map((row) => (
      <li
        key={`${row.page}-${row.date}-${row.description}-${row.amount}`}
        className="flex items-center justify-between gap-3 px-3 py-2"
      >
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {formatDate(row.date)}
        </span>
        <span className="min-w-0 flex-1 truncate text-foreground">
          {row.description}
        </span>
        <span className="shrink-0 text-foreground tabular-nums">
          {numberToCurrency(row.amount)}
        </span>
      </li>
    ))}
  </ul>
)

const NoteLine = ({ note }: { note: string }) => (
  <div>
    <p className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
      Your note
    </p>
    <p className="text-muted-foreground text-xs">{note}</p>
  </div>
)

const VerdictColumn = ({
  caption,
  snapshot,
  rowCount,
}: {
  caption: string
  snapshot: { status: string; accounts: { gap: number | null }[] }
  rowCount: number
}) => {
  const settled =
    snapshot.status.toUpperCase() === RECONCILIATION_STATUSES.reconciled
  return (
    <div className="px-3 py-2">
      <p className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
        {caption}
      </p>
      <p
        className={cn(
          'text-sm font-medium tabular-nums',
          settled ? 'text-emerald-600' : 'text-amber-700',
        )}
      >
        {settled ? 'Balanced' : gapVerdict(totalGap(snapshot.accounts))}
      </p>
      <p className="text-muted-foreground text-xs tabular-nums">
        {rowCount} {rowCount === 1 ? 'transaction' : 'transactions'}
      </p>
    </div>
  )
}

const CheckProgress = () => (
  <div className="space-y-1 rounded-md border-brand border-l-4 bg-brand/5 px-3 py-2">
    <p className="flex items-center gap-2 font-medium text-blue-7">
      <Loader />
      Reading the statement and your fix
    </p>
    <p className="text-ink-subtle text-xs">This takes about 15 seconds.</p>
  </div>
)

const AddingProgress = ({ missingRowCount }: { missingRowCount: number }) => (
  <div className="rounded-md border-brand border-l-4 bg-brand/5 px-3 py-2">
    <p className="flex items-center gap-2 font-medium text-blue-7">
      <Loader />
      Adding the missing {missingRowCount === 1 ? 'line' : 'lines'} and checking
      the balance
    </p>
  </div>
)

const otherStatementsLabel = (count: number) =>
  count === 1 ? 'other statement' : `${count} other statements`

const allOthersBalanceLine = (count: number) =>
  count === 1
    ? 'The other statement with this format already balances.'
    : `The other ${count} statements with this format already balance.`

const triageMessage = (triage: InstructionCheck) =>
  triage.question ||
  'We couldn\'t tell what to change from that. Name the line or section on the statement, like "the Interest Charged line".'
